import React, { useEffect, useState } from 'react';
import { TableRow, TableCell, Paper } from '@mui/material';
import Table from '../../../components/Table';
import { convertSecondToTime, formatDate } from '../../../util';
import { TableResponse } from '../../../schema/helper';
import { api } from '../../../api';
import dayjs from 'dayjs';

interface WorkingTimeReport {
  id: number;
  person_id: number;
  person_type_id: number;
  personnal_id: string;
  company_id: number;
  company: string;
  first_name: string;
  last_name: string;
  name: string;
  person_type: string;
  position: string;
  supervisor: string;
  work_start_time: string;
  work_end_time: string;
  total_time: number;
  second_before_17: number;
  second_after_17: number;
}

const tableHead = [
  'ชื่อ',
  'ประเภทบุคคล',
  'บริษัท',
  'ตำแหน่ง',
  'ผู้ควบคุมงาน',
  'วันที่',
  'ชั่วโมงทำงาน (08:00 - 17:00)',
  'ชั่วโมงทำงาน (17:00 - 8:00)',
  'ชั่วโมงการทำงานทั้งหมด'
];

interface TableSummaryProps {
  filterID: string;
  filterName: string;
  filterCompanyIDList: string[];
  filterPersonTypeIDList: string[];
  filterStartDate: Date | null;
  filterEndDate: Date | null;
}

const TableSummary: React.FC<TableSummaryProps> = ({
  filterID,
  filterName,
  filterCompanyIDList,
  filterPersonTypeIDList,
  filterStartDate,
  filterEndDate
}: TableSummaryProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [data, setData] = useState<TableResponse<WorkingTimeReport> | null>(null);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      const searchParams = new URLSearchParams();

      if (filterID) {
        searchParams.set('personal_id', `${filterID}`);
      }
      if (filterName) {
        searchParams.set('name', `${filterName}`);
      }
      if (filterCompanyIDList.length) {
        filterCompanyIDList.forEach((company_id) => {
          searchParams.append('company_id', `${company_id}`);
        });
      }
      if (filterPersonTypeIDList.length) {
        filterPersonTypeIDList.forEach((person_type_id) => {
          searchParams.append('person_type_id', `${person_type_id}`);
        });
      }
      if (filterStartDate) {
        searchParams.set('start_date', `${formatDate(filterStartDate)}`);
      }
      if (filterEndDate) {
        searchParams.set('end_date', `${formatDate(filterEndDate)}`);
      }

      searchParams.set('offset', `${page * rowsPerPage}`);
      searchParams.set('limit', `${rowsPerPage}`);
      setIsLoading(true)
      api
        .get<TableResponse<WorkingTimeReport>>(`/working-time`, { params: searchParams })
        .then((res) => {
          const { data } = res;
          setData(data);
        })
        .finally(() => setIsLoading(false));
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [
    filterID,
    filterName,
    filterCompanyIDList,
    filterPersonTypeIDList,
    filterStartDate,
    filterEndDate,
    page,
    rowsPerPage
  ]);

  return (
    <Paper>
      <Table
        heads={tableHead}
        count={data?.total ? data.total : 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        isLoading={isLoading}
      >
        {data?.data.map((data, index) => {
          return (
            <TableRow key={index}>
              <TableCell align="center">{data.name}</TableCell>
              <TableCell align="center">{data.person_type}</TableCell>
              <TableCell align="center">{data.company}</TableCell>
              <TableCell align="center">{data.position}</TableCell>
              <TableCell align="center">{data?.supervisor}</TableCell>
              <TableCell align="center">
                {dayjs(data.work_start_time).format('DD/MM/YYYY')}
              </TableCell>
              <TableCell align="center">
                {convertSecondToTime(data.second_before_17)}
              </TableCell>
              <TableCell align="center">{convertSecondToTime(data.second_after_17)}</TableCell>
              <TableCell align="center">{convertSecondToTime(data.total_time)}</TableCell>
            </TableRow>
          );
        })}
      </Table>
    </Paper>
  );
};

export default TableSummary;
